import React, { useMemo, useState, useEffect } from 'react';
import { normalizarFechaTareo } from '../utils/tareoEventoEfectivo';

/**
 * Modal: partir boleta interna en 2 tramos por fechas + destino descuento PLAME.
 */
const BoletaInternaTramosModal = ({
  isOpen,
  onClose,
  empleados = [],
  tareosPeriodo = [],
  tramosMap = {},
  onGuardar,
  guardando = false,
}) => {
  const empleadosInterna = useMemo(
    () =>
      (empleados || []).filter((emp) => {
        const tipo = String(emp?.tipo_planilla || emp?.datosEmpleado?.tipo_planilla || '')
          .toLowerCase()
          .trim();
        if (tipo === 'dual' || tipo === 'solo_interna') return true;
        return emp?.sueldo_interno != null && Number(emp.sueldo_interno) > 0;
      }),
    [empleados]
  );

  const [empleadoId, setEmpleadoId] = useState(null);
  const [activo, setActivo] = useState(false);
  const [fechasTramo1, setFechasTramo1] = useState([]);
  const [sueldo1, setSueldo1] = useState('');
  const [sueldo2, setSueldo2] = useState('');
  const [destino, setDestino] = useState('interna_1');

  useEffect(() => {
    if (!isOpen) return;
    if (empleadosInterna.length > 0 && !empleadoId) {
      setEmpleadoId(empleadosInterna[0].id);
    }
  }, [isOpen, empleadosInterna, empleadoId]);

  useEffect(() => {
    if (!empleadoId) return;
    const cfg = tramosMap[String(empleadoId)];
    const emp = empleadosInterna.find((e) => Number(e.id) === Number(empleadoId));
    const sueldoDef = emp?.sueldo_interno != null ? String(emp.sueldo_interno) : '';
    if (cfg?.activo) {
      setActivo(true);
      setFechasTramo1(cfg.fechas_tramo_1 || []);
      setSueldo1(cfg.sueldo_interno_1 != null ? String(cfg.sueldo_interno_1) : sueldoDef);
      setSueldo2(cfg.sueldo_interno_2 != null ? String(cfg.sueldo_interno_2) : sueldoDef);
      setDestino(cfg.destino_descuentos_plame || 'interna_1');
    } else {
      setActivo(false);
      setFechasTramo1([]);
      setSueldo1(sueldoDef);
      setSueldo2(sueldoDef);
      setDestino('interna_1');
    }
  }, [empleadoId, tramosMap, empleadosInterna]);

  const diasEmpleado = useMemo(() => {
    if (!empleadoId) return [];
    const idNum = Number(empleadoId);
    const porFecha = new Map();
    (tareosPeriodo || []).forEach((t) => {
      if (Number(t.empleado_id) !== idNum) return;
      const fecha = normalizarFechaTareo(t.fecha);
      if (!fecha) return;
      if (!porFecha.has(fecha)) {
        porFecha.set(fecha, {
          fecha,
          tipo_tareo: String(t.tipo_evento || 'asistio').toLowerCase(),
        });
      }
    });
    return Array.from(porFecha.values()).sort((a, b) => a.fecha.localeCompare(b.fecha));
  }, [empleadoId, tareosPeriodo]);

  const toggleFecha = (fecha) => {
    setFechasTramo1((prev) => {
      if (prev.includes(fecha)) return prev.filter((f) => f !== fecha);
      return [...prev, fecha].sort();
    });
  };

  const marcarPrimeraMitad = () => {
    const mid = Math.ceil(diasEmpleado.length / 2);
    setFechasTramo1(diasEmpleado.slice(0, mid).map((d) => d.fecha));
  };

  const handleGuardar = async () => {
    if (!empleadoId) return;
    if (activo) {
      if (fechasTramo1.length === 0) {
        alert('Marca al menos un día para Interna 1 (el resto irá a Interna 2).');
        return;
      }
      if (fechasTramo1.length >= diasEmpleado.length && diasEmpleado.length > 0) {
        alert('Deja al menos un día para Interna 2.');
        return;
      }
      const s1 = parseFloat(sueldo1);
      const s2 = parseFloat(sueldo2);
      if (!Number.isFinite(s1) || s1 < 0 || !Number.isFinite(s2) || s2 < 0) {
        alert('Ingresa sueldos válidos para ambas internas.');
        return;
      }
      await onGuardar({
        empleados: [
          {
            id: Number(empleadoId),
            activo: true,
            fechas_tramo_1: fechasTramo1,
            sueldo_interno_1: s1,
            sueldo_interno_2: s2,
            destino_descuentos_plame: destino,
          },
        ],
      });
    } else {
      await onGuardar({
        empleados: [{ id: Number(empleadoId), activo: false }],
        eliminar: [Number(empleadoId)],
      });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="bg-gradient-to-r from-amber-600 to-orange-500 p-5 text-white flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold">Boleta interna en dos partes</h2>
            <p className="text-amber-100 text-sm">
              Parte los días en dos internas con sueldo distinto y elige dónde va el descuento PLAME
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-white hover:bg-white/20 rounded-lg px-3 py-1 text-xl"
          >
            ✕
          </button>
        </div>

        <div className="p-5 overflow-y-auto flex-1 space-y-4">
          {empleadosInterna.length === 0 ? (
            <p className="text-center text-gray-500 py-8">
              No hay personal con boleta interna en esta planilla.
            </p>
          ) : (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Empleado</label>
                <select
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  value={empleadoId || ''}
                  onChange={(e) => setEmpleadoId(Number(e.target.value) || null)}
                >
                  {empleadosInterna.map((emp) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.nombre_completo || emp.nombre || `Empleado ${emp.id}`}
                      {emp.documento || emp.numero_documento
                        ? ` (${emp.documento || emp.numero_documento})`
                        : ''}
                    </option>
                  ))}
                </select>
              </div>

              <label className="flex items-center gap-2 text-sm font-medium text-gray-800">
                <input
                  type="checkbox"
                  checked={activo}
                  onChange={(e) => setActivo(e.target.checked)}
                />
                Activar boleta interna en dos partes para este personal
              </label>

              {activo && (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Sueldo Interna 1
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                        value={sueldo1}
                        onChange={(e) => setSueldo1(e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Sueldo Interna 2
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                        value={sueldo2}
                        onChange={(e) => setSueldo2(e.target.value)}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Descuento PLAME (ONP/AFP) va a
                    </label>
                    <select
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      value={destino}
                      onChange={(e) => setDestino(e.target.value)}
                    >
                      <option value="interna_1">Interna 1</option>
                      <option value="interna_2">Interna 2</option>
                      <option value="ninguno">Ninguna (omitir en ambas)</option>
                    </select>
                  </div>

                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900">
                    Marca los días de <strong>Interna 1</strong>. El resto va a Interna 2.
                    El sueldo básico (0121) se prorratea por los días de cada parte:
                    (sueldo ÷ días del mes) × días del tramo. Faltas y eventos van a la parte de su fecha.
                  </div>

                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={marcarPrimeraMitad}
                      className="text-xs px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-lg"
                    >
                      Primera mitad → Interna 1
                    </button>
                    <button
                      type="button"
                      onClick={() => setFechasTramo1([])}
                      className="text-xs px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-lg"
                    >
                      Limpiar
                    </button>
                    <span className="text-xs text-gray-500 self-center ml-auto">
                      Interna 1: {fechasTramo1.length} · Interna 2:{' '}
                      {Math.max(0, diasEmpleado.length - fechasTramo1.length)}
                    </span>
                  </div>

                  {diasEmpleado.length === 0 ? (
                    <p className="text-center text-gray-500 py-6">Sin tareos en el período.</p>
                  ) : (
                    <div className="overflow-x-auto border border-gray-200 rounded-lg max-h-64 overflow-y-auto">
                      <table className="min-w-full text-sm">
                        <thead className="bg-gray-50 text-gray-700 sticky top-0">
                          <tr>
                            <th className="px-3 py-2 text-left">Interna 1</th>
                            <th className="px-3 py-2 text-left">Fecha</th>
                            <th className="px-3 py-2 text-left">Tareo</th>
                            <th className="px-3 py-2 text-left">Parte</th>
                          </tr>
                        </thead>
                        <tbody>
                          {diasEmpleado.map((dia) => {
                            const en1 = fechasTramo1.includes(dia.fecha);
                            return (
                              <tr
                                key={dia.fecha}
                                className={`border-t border-gray-100 ${en1 ? 'bg-amber-50' : ''}`}
                              >
                                <td className="px-3 py-2">
                                  <input
                                    type="checkbox"
                                    checked={en1}
                                    onChange={() => toggleFecha(dia.fecha)}
                                  />
                                </td>
                                <td className="px-3 py-2 font-medium whitespace-nowrap">{dia.fecha}</td>
                                <td className="px-3 py-2 text-gray-600">{dia.tipo_tareo}</td>
                                <td className="px-3 py-2">
                                  {en1 ? (
                                    <span className="text-amber-700 font-medium">Interna 1</span>
                                  ) : (
                                    <span className="text-orange-700 font-medium">Interna 2</span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>

        <div className="bg-gray-50 px-5 py-4 border-t flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
            disabled={guardando}
          >
            Cerrar
          </button>
          <button
            type="button"
            onClick={handleGuardar}
            disabled={guardando || empleadosInterna.length === 0}
            className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50"
          >
            {guardando ? 'Guardando…' : 'Guardar y recalcular'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BoletaInternaTramosModal;
